import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import Home from "@/pages/Home";
import Fleet from "@/pages/Fleet";
import CarDetail from "@/pages/CarDetail";
import Contact from "@/pages/Contact";
import LoganAirport from "@/pages/LoganAirport";
import LoganPickupDirections from "@/pages/LoganPickupDirections";
import AirportDropoff from "@/pages/AirportDropoff";
import LocalLanding from "@/pages/LocalLanding";
import LocationsDirectory from "@/pages/LocationsDirectory";
import ListYourCar from "@/pages/ListYourCar";
import BecomeAHost from "@/pages/BecomeAHost";
import HostLanding from "@/pages/HostLanding";
import CityHostPage from "@/pages/CityHostPage";
import HostGuidesHub from "@/pages/HostGuidesHub";
import HostGuide from "@/pages/HostGuide";
import { HOST_LANDING_SLUGS } from "@/data/hostLandingPages";
import { PT_SLUG_MAP } from "@/data/hostLandingPagesPt";
import { CITY_HOST_SLUGS } from "@/data/cityHostPages";
import { PT_CITY_HOST_SLUGS, ptCityHostSlug } from "@/data/cityHostPagesPt";
import { HOST_GUIDE_SLUGS } from "@/data/hostGuides";
import { LANDING_PAGES } from "@/data/landingPages";
import CityHub from "@/components/CityHub";
import CityServicePage from "@/components/CityServicePage";
import { CITIES, hubSlug, generatedServiceSlugs } from "@/data/cityLocations";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Account from "@/pages/Account";
import HostOnboarding from "@/pages/HostOnboarding";
import HostPortal from "@/pages/HostPortal";
import PublicProfile from "@/pages/PublicProfile";
import VerifyDriver from "@/pages/VerifyDriver";
import AnalyticsTracker from "@/components/AnalyticsTracker";

const ProtectedRoute = ({ children, role, redirect = "/login" }) => {
  const { user, checked } = useAuth();
  if (!checked)
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500">
        Loading…
      </div>
    );
  if (!user) return <Navigate to={redirect} replace />;
  const isAdminRole = user.role === "admin" || user.role === "operations_manager";
  const allowed = !role || user.role === role || (role === "admin" && isAdminRole);
  if (!allowed) return <Navigate to={isAdminRole ? "/admin" : "/account"} replace />;
  return children;
};

const PublicLayout = ({ children }) => (
  <>
    <Navbar />
    <main>{children}</main>
    <Footer />
  </>
);

// Reset scroll to top on every route change (prevents landing mid-page).
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <FavoritesProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AnalyticsTracker />
          <ImpersonationBanner />
          <Routes>
            <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
            <Route path="/fleet" element={<PublicLayout><Fleet /></PublicLayout>} />
            <Route path="/cars/:id" element={<PublicLayout><CarDetail /></PublicLayout>} />
            <Route path="/book/:id" element={<PublicLayout><CarDetail /></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
            <Route path="/logan-airport-car-rental" element={<PublicLayout><LoganAirport /></PublicLayout>} />
            <Route path="/logan-airport-car-rental-pickup-directions" element={<PublicLayout><LoganPickupDirections /></PublicLayout>} />
            <Route path="/logan-airport-drop-off" element={<PublicLayout><AirportDropoff /></PublicLayout>} />
            <Route path="/car-rental-locations" element={<PublicLayout><LocationsDirectory /></PublicLayout>} />
            <Route path="/list-your-car-massachusetts" element={<PublicLayout><ListYourCar /></PublicLayout>} />
            <Route path="/become-a-host" element={<PublicLayout><BecomeAHost /></PublicLayout>} />
            {CITY_HOST_SLUGS.map((c) => (
              <Route key={c} path={`/become-a-host/${c}-ma`} element={<PublicLayout><CityHostPage citySlug={c} /></PublicLayout>} />
            ))}
            {PT_CITY_HOST_SLUGS.map((c) => (
              <Route key={`pt-${c}`} path={`/${ptCityHostSlug(c)}`} element={<PublicLayout><CityHostPage citySlug={c} lang="pt" /></PublicLayout>} />
            ))}
            <Route path="/host-guides" element={<PublicLayout><HostGuidesHub /></PublicLayout>} />
            {HOST_GUIDE_SLUGS.map((s) => (
              <Route key={s} path={`/host-guides/${s}`} element={<PublicLayout><HostGuide slug={s} /></PublicLayout>} />
            ))}
            <Route path="/hosts" element={<Navigate to="/become-a-host" replace />} />
            <Route path="/list-your-car" element={<Navigate to="/become-a-host" replace />} />
            <Route path="/become-a-partner" element={<Navigate to="/become-a-host" replace />} />
            <Route path="/host-application" element={<Navigate to="/host" replace />} />
            <Route path="/host-signup" element={<Navigate to="/host-application" replace />} />
            {HOST_LANDING_SLUGS.map((s) => (
              <Route key={s} path={`/${s}`} element={<PublicLayout><HostLanding slug={s} /></PublicLayout>} />
            ))}
            {Object.entries(PT_SLUG_MAP).map(([enSlug, ptSlug]) => (
              <Route key={ptSlug} path={`/pt/${ptSlug}`} element={<PublicLayout><HostLanding slug={enSlug} lang="pt" /></PublicLayout>} />
            ))}
            <Route path="/boston-airport-car-rental" element={<Navigate to="/logan-airport-car-rental" replace />} />
            <Route path="/turo-alternative-boston" element={<Navigate to="/economy-car-rental-boston" replace />} />
            {Object.keys(LANDING_PAGES).filter((slug) => slug !== "boston-airport-car-rental").map((slug) => (
              <Route key={slug} path={`/${slug}`} element={<PublicLayout><LocalLanding slug={slug} /></PublicLayout>} />
            ))}
            {/* Greater Boston local SEO: 16 city hubs + 45 service child pages, from cityLocations config */}
            {Object.values(CITIES).map((c) => (
              <Route key={c.citySlug} path={`/${hubSlug(c.citySlug)}`} element={<PublicLayout><CityHub citySlug={c.citySlug} /></PublicLayout>} />
            ))}
            {generatedServiceSlugs().map(({ citySlug, service, slug }) => (
              <Route key={slug} path={`/${slug}`} element={<PublicLayout><CityServicePage citySlug={citySlug} service={service} /></PublicLayout>} />
            ))}
            <Route path="/weekly-car-rental-boston" element={<Navigate to="/boston-weekly-car-rental" replace />} />
            <Route path="/u/:id" element={<PublicLayout><PublicProfile /></PublicLayout>} />
            <Route path="/verify-driver/:token" element={<PublicLayout><VerifyDriver /></PublicLayout>} />
            <Route path="/host" element={<ProtectedRoute><PublicLayout><HostOnboarding /></PublicLayout></ProtectedRoute>} />
            <Route path="/host/dashboard" element={<ProtectedRoute><PublicLayout><HostPortal /></PublicLayout></ProtectedRoute>} />
            <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />
            <Route path="/register" element={<PublicLayout><Register /></PublicLayout>} />
            <Route path="/forgot-password" element={<PublicLayout><ForgotPassword /></PublicLayout>} />
            <Route path="/reset-password" element={<PublicLayout><ResetPassword /></PublicLayout>} />
            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <PublicLayout><Account /></PublicLayout>
                </ProtectedRoute>
              }
            />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute role="admin" redirect="/admin/login">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
          </Routes>
        </BrowserRouter>
        </FavoritesProvider>
        <Toaster position="top-right" theme="light" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
